import type { KursDto } from "@shared/kosztyTypy.js";
import {
  MAKS_DLUGOSC_SZUKANIA_WYWOLAN,
  OKNO_STANU_MS,
  type KosztWywolaniaDto,
  type PodgladSondyDto,
  type StanTresciDto,
  type WynikSondyDto,
  type WywolanieWierszDto,
} from "@shared/wywolaniaTypy.js";
import type { WariantBadge } from "../components/ui/Badge.js";
import { formatujLiczbe, formatujPln, formatujUsd } from "./koszty.js";
import { odmien } from "./polski.js";

/**
 * Zakladka Developer: etykiety i format po polsku, bez DOM i bez Reacta (poza typem wariantu plakietki).
 *
 * Zasada projektu "nie pokazujemy liczby, ktorej nie ufamy" dziala tu tak samo jak w kosztach: brak danych to kreska
 * z powodem, a nigdy zero. Kwota wywolania to szacunek z cennika, wiec ma te same reguly co na stronie Koszty.
 */

const dwie = (n: number): string => String(n).padStart(2, "0");

/** ISO -> "2026-10-04 15:04:05" w czasie lokalnym przegladarki. Zly napis wraca bez zmian, nie jako "Invalid Date". */
export function formatujCzas(iso: string | null): string {
  if (iso === null) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${dwie(d.getMonth() + 1)}-${dwie(d.getDate())} ${dwie(d.getHours())}:${dwie(d.getMinutes())}:${dwie(d.getSeconds())}`;
}

/** 850 -> "850 ms", 1234 -> "1,2 s", 65000 -> "65,0 s". null to kreska (nie znamy), nie zero. */
export function formatujMs(ms: number | null): string {
  if (ms === null) return "-";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
}

/** 90 -> "1 min 30 s", 3700 -> "1 h 1 min". Do uptime i czasu oczekiwania w kolejce. */
export function formatujSekundy(sek: number): string {
  const s = Math.max(0, Math.round(sek));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${s % 60} s`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

/** Skrot identyfikatora do tabel: pierwsze 8 znakow. Pelne id jest w szczegole i w tytule (dymku). */
export function skrocId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}...` : id;
}

export function etykietaStatusu(status: string): { etykieta: string; wariant: WariantBadge } {
  switch (status) {
    case "OK":
      return { etykieta: "Udane", wariant: "success" };
    case "BLAD":
      return { etykieta: "Błąd", wariant: "danger" };
    case "WYSLANE":
      return { etykieta: "W toku", wariant: "info" };
    case "PORZUCONE":
      return { etykieta: "Porzucone", wariant: "warning" };
    default:
      return { etykieta: status, wariant: "neutralny" };
  }
}

/** Tokeny wejscia i wyjscia w jednym napisie: "262 000 / 9 800", brak jednej strony to kreska. */
export function formatujTokenyWiersza(wejscie: number | null, wyjscie: number | null, formatuj: (n: number) => string): string {
  if (wejscie === null && wyjscie === null) return "-";
  const czesc = (n: number | null): string => (n === null ? "-" : formatuj(n));
  return `${czesc(wejscie)} / ${czesc(wyjscie)}`;
}

export interface KosztDoWyswietlenia {
  usd: string | null;
  pln: string | null;
  /** Powod do dymka: czemu nie ma kwoty (kreska) albo czemu kwota jest tylko dolnym ograniczeniem ("od <kwota>"). */
  powod: string | null;
}

/** Koszt jednego wywolania: kwota z drugim wierszem w zlotych, albo kreska z powodem. Zero tylko wtedy, gdy policzone. */
export function kosztWywolaniaDoPokazania(koszt: KosztWywolaniaDto, kurs: KursDto | null): KosztDoWyswietlenia {
  if (koszt.rodzaj === "policzony") {
    // Dolne ograniczenie ("od <kwota>") nigdy nie wyglada jak dokladna kwota: ten sam wzorzec co karta kosztu analizy.
    const prefiks = koszt.dolnaGranica === undefined ? "" : "od ";
    return {
      usd: `${prefiks}${formatujUsd(koszt.usd)}`,
      pln: kurs ? `${prefiks}${formatujPln(koszt.usd, kurs.kurs)}` : null,
      powod: koszt.dolnaGranica ?? null,
    };
  }
  if (koszt.rodzaj === "brak") return { usd: null, pln: null, powod: koszt.powod };
  return {
    usd: null,
    pln: null,
    powod: "nieudana próba bez zapisanego zużycia: poza kosztem (dostawca mógł ją naliczyć, ale nie wiemy ile)",
  };
}

/** Zakres strony: "1-25 z 312". Pusta lista to "0 z 0", a nie "1-0 z 0". */
export function zakresStrony(offset: number, ileNaStronie: number, razem: number): string {
  if (razem === 0 || ileNaStronie === 0) return "0 z 0";
  return `${offset + 1}-${offset + ileNaStronie} z ${razem}`;
}

/** Jedno zdanie o stanie tresci, gdy tresci nie mozna pokazac. */
export function opisBrakuTresci(stan: StanTresciDto): string | null {
  switch (stan.stan) {
    case "zapisana":
      return null;
    case "wygasla":
      return `Treść wygasła zgodnie z polityką retencji (${formatujCzas(stan.wygaslo)}). Zostaje suma kontrolna, rozmiar i metadane.`;
    case "w-toku":
      return "Wywołanie jeszcze trwa, odpowiedzi nie ma.";
    case "niezapisana":
      return `Treści nie ma: ${stan.powod}.`;
  }
}

const ZRODLA: Readonly<Record<string, string>> = {
  ewidencja: "z ewidencji wywołania",
  snapshot: "z katalogu poleceń",
  ocena: "z przebiegu oceny tej sesji",
};

export function etykietaZrodlaTresci(zrodlo: string): string {
  return ZRODLA[zrodlo] ?? zrodlo;
}

/** Okresy w filtrze listy: etykieta i liczba godzin wstecz (null = caly zapis). */
export const OKRESY_FILTRA: readonly { wartosc: string; etykieta: string; godziny: number | null }[] = [
  { wartosc: "", etykieta: "Cały zapis", godziny: null },
  { wartosc: "24h", etykieta: "Ostatnie 24 godziny", godziny: 24 },
  { wartosc: "7d", etykieta: "Ostatnie 7 dni", godziny: 7 * 24 },
  { wartosc: "30d", etykieta: "Ostatnie 30 dni", godziny: 30 * 24 },
];

/** Poczatek okresu jako ISO dla parametru `od`, albo undefined dla calego zapisu i nieznanej wartosci. */
export function odDlaOkresu(okres: string, teraz: Date = new Date()): string | undefined {
  const o = OKRESY_FILTRA.find((x) => x.wartosc === okres);
  if (!o || o.godziny === null) return undefined;
  return new Date(teraz.getTime() - o.godziny * 3_600_000).toISOString();
}

export interface LiniaLoguPodzielona {
  czas: string | null;
  poziom: string | null;
  tresc: string;
}

/** Linia w formacie loggera: `2026-08-13T00:00:00.000Z [warn] tresc`. Inna linia (slad stosu) zostaje cala w `tresc`. */
export function podzielLinieLogu(linia: string): LiniaLoguPodzielona {
  const m = /^(\S+) \[(\w+)\] (.*)$/s.exec(linia);
  return m ? { czas: m[1], poziom: m[2], tresc: m[3] } : { czas: null, poziom: null, tresc: linia };
}

/** Klucze linii niezalezne od polozenia w oknie: linia z milisekundami jest unikalna, a identyczne dostaja kolejny numer. */
export function kluczeLinii(linie: readonly string[]): string[] {
  const wystapienia = new Map<string, number>();
  return linie.map((linia) => {
    const n = (wystapienia.get(linia) ?? 0) + 1;
    wystapienia.set(linia, n);
    return `${n}|${linia}`;
  });
}

/**
 * Liczba z rzeczownikiem w poprawnej odmianie ("1 wpis", "3 wpisy", "5 wpisow", "12 wpisow", "22 wpisy"). Jedna funkcja na
 * cala zakladke, zamiast `n === 1 ? "wpis" : "wpisy"` w kazdym miejscu (to dawalo "5 wpisy"). Regule 1 / 2-4 / reszta, z
 * wyjatkiem 12-14, trzyma `odmien` z lib/polski.
 */
export function liczbaZOdmiana(n: number, jeden: string, kilka: string, wiele: string): string {
  return `${formatujLiczbe(n)} ${odmien(n, jeden, kilka, wiele)}`;
}

export const liczbaWpisow = (n: number): string => liczbaZOdmiana(n, "wpis", "wpisy", "wpisów");

/** `Date.getTimezoneOffset()` (minuty, ze znakiem odwrotnym do UTC+N) jako "UTC+2", "UTC-5:30" albo "UTC". */
export function etykietaOffsetuUtc(minutyGetTimezoneOffset: number): string {
  const minuty = -minutyGetTimezoneOffset;
  if (minuty === 0) return "UTC";
  const abs = Math.abs(minuty);
  const m = abs % 60;
  return `UTC${minuty > 0 ? "+" : "-"}${Math.floor(abs / 60)}${m > 0 ? `:${dwie(m)}` : ""}`;
}

/** Strefa przegladarki do opisu godzin: "Europe/Warsaw, UTC+2". Gdy Intl nie poda nazwy, zostaje samo przesuniecie. */
export function strefaPrzegladarki(teraz: Date = new Date()): string {
  const przesuniecie = etykietaOffsetuUtc(teraz.getTimezoneOffset());
  try {
    const nazwa = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return nazwa ? `${nazwa}, ${przesuniecie}` : przesuniecie;
  } catch {
    return przesuniecie;
  }
}

// ---------------------------------------------------------------------------
// Bledy API po polsku
// ---------------------------------------------------------------------------

/**
 * Kazdy kod, ktory serwer moze oddac pod /api/developer (i jego otoczenie: sesja, bramka bledow), z komunikatem po polsku.
 * Uzytkownik nie ma widziec "zly_parametr" ani "(siec)": dla niego to nic nie mowi. Test pilnuje, zeby kazdy kod z
 * src/routes/developerApi.ts mial tu wpis (nowy kod bez komunikatu wywala test, zamiast wyjsc na ekran jako surowy napis).
 * "siec" to kod klienta: fetch rzucil wyjatek zanim odpowiedz doszla.
 */
const KOMUNIKATY_BLEDOW_API: Readonly<Record<string, string>> = {
  siec: "Brak połączenia z serwerem. Sprawdź połączenie z internetem i spróbuj ponownie.",
  zly_parametr: `Serwer odrzucił jeden z filtrów (np. szukany tekst dłuższy niż ${MAKS_DLUGOSC_SZUKANIA_WYWOLAN} znaków). Zmień albo wyczyść filtry.`,
  brak_potwierdzenia: "Serwer nie dostał potwierdzenia płatnej sondy. Otwórz okno sondy i potwierdź jeszcze raz.",
  nie_znaleziono: "Nie znaleziono tego, o co poproszono (wiersz mógł zostać usunięty razem z analizą).",
  sonda_w_toku: "Sonda już trwa. Poczekaj na jej wynik.",
  za_czesto: "Sonda była uruchomiona przed chwilą. Poczekaj kilka sekund i spróbuj ponownie.",
  unauthorized: "Sesja wygasła. Zaloguj się ponownie.",
  blad_serwera: "Serwer zgłosił błąd. Spróbuj ponownie za chwilę, a jeśli wraca, sprawdź zakładkę Logi.",
  zly_json: "Serwer nie zrozumiał zapytania. Odśwież stronę i spróbuj ponownie.",
  zle_zadanie: "Serwer nie zrozumiał zapytania. Odśwież stronę i spróbuj ponownie.",
  cialo_za_duze: "Zapytanie jest za duże dla serwera.",
  unknown: "Serwer odpowiedział w nieoczekiwany sposób (może być chwilowo niedostępny). Spróbuj ponownie za chwilę.",
};

export const KODY_BLEDOW_API: readonly string[] = Object.keys(KOMUNIKATY_BLEDOW_API);

/** Komunikat dla kodu z `ApiError.code` albo "siec". Nieznany kod to ogolny komunikat z kodem na koncu (do zgloszenia). */
export function komunikatBleduApi(kod: string): string {
  return KOMUNIKATY_BLEDOW_API[kod] ?? `Nieoczekiwany błąd (kod: ${kod}). Spróbuj ponownie.`;
}

// ---------------------------------------------------------------------------
// Okno czasowe w liscie wywolan
// ---------------------------------------------------------------------------

/** Czy napis jest znacznikiem czasu, ktory serwer przyjmie w `od`/`do` (zly napis z adresu nie moze isc do zapytania). */
export function czyZnacznikCzasu(s: string): boolean {
  return s !== "" && !Number.isNaN(Date.parse(s));
}

/** Poczatek okna "ostatniej doby" ze Stanu systemu: dokladnie ta granica, od ktorej serwer policzyl kafelki. */
export function odOknaStanu(wygenerowano: string): string {
  return new Date(Date.parse(wygenerowano) - OKNO_STANU_MS).toISOString();
}

/**
 * Adres listy bledow z kafelka "Bledy" z ostatniej doby. Niesie to samo okno co kafelek (`od`), wiec liczba wierszy listy
 * zgadza sie z liczba na kafelku. Bez tego link prowadzil do bledow z calego zapisu (42 zamiast 39).
 */
export function adresBledowZDoby(wygenerowano: string): string {
  const q = new URLSearchParams({ karta: "wywolania", status: "BLAD", od: odOknaStanu(wygenerowano) });
  return `/developer?${q.toString()}`;
}

const SAMA_DATA = /^\d{4}-\d{2}-\d{2}$/;

/** "od 2026-10-03 14:00:00 do ..." dla aktywnego okna czasowego: znaczniki w czasie lokalnym, sama data bez zmian. */
export function opisOknaCzasowego(od: string, doDaty: string): string {
  const granica = (s: string): string => (SAMA_DATA.test(s) ? s : formatujCzas(s));
  const czesci: string[] = [];
  if (od !== "") czesci.push(`od ${granica(od)}`);
  if (doDaty !== "") czesci.push(`do ${granica(doDaty)}`);
  return czesci.join(" ");
}

// ---------------------------------------------------------------------------
// Sprzecznosc w zapisie wywolania
// ---------------------------------------------------------------------------

export interface SprzecznoscStatusu {
  /** Krotka etykieta do plakietki. */
  etykieta: string;
  /** Jedno zdanie: co z czym sie nie zgadza. */
  opis: string;
}

/**
 * Wiersz oznaczony jako UDANY (status OK), a zapis przeczy temu sam sobie. Dane historyczne maja takie wiersze (pierwsze
 * wiersze dziennika z 17.08.2026: kolumna `status` powstala dopiero 19.08 z wartoscia domyslna OK dla wszystkich istniejacych
 * wierszy, takze nieudanych), wiec interfejs mowi o tym wprost, zamiast pokazywac zielone "Udane" przy bledzie.
 *
 * Reguly sa WYLACZNIE takie, ktore nie maja legalnego odpowiednika w biezacym kodzie zapisu:
 * - HTTP >= 400 bez powodu zakonczenia. Z powodem (`too_long` 413, `model_loading` 503 z warstwy sygnalowej) jest zgodny z
 *   kontraktem: wywolanie UDANE z adnotacja, wiec nie jest sprzecznoscia;
 * - flaga `ok` = false (udane nie moze nie byc ok);
 * - komunikat bledu przy udanym (zaden zapis `ok: true` go nie ustawia).
 * Kolejnosc to kolejnosc konkretu: kod HTTP mowi wiecej niz sama flaga.
 */
export function sprzecznoscStatusu(
  w: Pick<WywolanieWierszDto, "status" | "ok" | "httpStatus" | "powodZakonczenia" | "blad">,
): SprzecznoscStatusu | null {
  if (w.status !== "OK") return null;
  if (w.httpStatus !== null && w.httpStatus >= 400 && !w.powodZakonczenia) {
    return { etykieta: "Status sprzeczny z kodem HTTP", opis: `Zapis jest oznaczony jako udany, a dostawca zwrócił HTTP ${w.httpStatus}.` };
  }
  if (!w.ok) {
    return { etykieta: "Status sprzeczny z zapisem", opis: "Zapis jest oznaczony jako udany, ale jego znacznik powodzenia mówi, że wywołanie się nie udało." };
  }
  if (w.blad !== null && w.blad.trim() !== "") {
    return { etykieta: "Status sprzeczny z błędem", opis: "Zapis jest oznaczony jako udany, ale ma komunikat błędu." };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Sonda dostawcow: zapytania a dostawcy
// ---------------------------------------------------------------------------

const ROLE_SONDY: Readonly<Record<string, string>> = {
  stt: "transkrypcja",
  expression: "ocena brzmienia",
  ser: "wymiary głosu",
};

function polaczRole(role: readonly string[]): string {
  return role.length <= 1 ? (role[0] ?? "") : `${role.slice(0, -1).join(", ")} i ${role[role.length - 1]}`;
}

interface CelSondyDoOpisu {
  rodzaj: string;
  id: string;
  etykieta: string;
}

/**
 * Ile ZAPYTAN idzie do ilu DOSTAWCOW. To nie to samo: ten sam dostawca bywa sprawdzany w wiecej niz jednej roli (Gemini jako
 * transkrypcja i jako ocena brzmienia), wiec "odpyta 6 dostawcow" przy 5 dostawcach klamalo. Dostawcow liczymy z listy celow,
 * a zapytania z liczby od serwera; powtorzeni to ci, ktorzy maja wiecej niz jedna role.
 */
function podsumujSonde(zapytan: number, cele: readonly CelSondyDoOpisu[]): { dostawcow: number; powtorzeni: string[] } {
  const rolePerDostawca = new Map<string, { etykieta: string; role: string[] }>();
  for (const c of cele) {
    const wpis = rolePerDostawca.get(c.id) ?? { etykieta: c.etykieta, role: [] };
    wpis.role.push(ROLE_SONDY[c.rodzaj] ?? c.rodzaj);
    rolePerDostawca.set(c.id, wpis);
  }
  const powtorzeni = [...rolePerDostawca.values()].filter((d) => d.role.length > 1).map((d) => `${d.etykieta}: ${polaczRole(d.role)}`);
  return { dostawcow: Math.min(rolePerDostawca.size, zapytan), powtorzeni };
}

/** "6 zapytan do 5 dostawcow (Gemini: transkrypcja i ocena brzmienia)" albo, gdy sa rowne, samo "N dostawcow". */
function opisZapytanIDostawcow(
  zapytan: number,
  cele: readonly CelSondyDoOpisu[],
  czasownikRowne: string,
  czasownikRozne: string,
): string {
  const { dostawcow, powtorzeni } = podsumujSonde(zapytan, cele);
  if (dostawcow === zapytan) return `${czasownikRowne} ${zapytan} ${odmien(zapytan, "dostawcę", "dostawców", "dostawców")}`;
  const zapytania = liczbaZOdmiana(zapytan, "zapytanie", "zapytania", "zapytań");
  const dostawcy = `${dostawcow} ${odmien(dostawcow, "dostawcy", "dostawców", "dostawców")}`;
  return `${czasownikRozne} ${zapytania} do ${dostawcy}${powtorzeni.length > 0 ? ` (${powtorzeni.join("; ")})` : ""}`;
}

/** Tresc okna potwierdzenia sondy, z danych podgladu (nic na sztywno). */
export function trescPotwierdzeniaSondy(p: PodgladSondyDto): string {
  if (p.doWyslania === 0) return "Żaden dostawca nie jest skonfigurowany, więc nic nie zostanie wysłane.";
  const zapytania = opisZapytanIDostawcow(
    p.doWyslania,
    p.dostawcy.filter((d) => d.skonfigurowany),
    "Sonda odpyta",
    "Sonda wyśle",
  );
  if (p.platnych === 0) return `${zapytania}. Nic z tego nie jest płatne: to samo sprawdzenie łączności.`;
  const platne = `z czego ${p.platnych} to ${odmien(p.platnych, "płatne wywołanie", "płatne wywołania", "płatnych wywołań")} modelu`;
  return p.platnych >= p.doWyslania ? `${zapytania}, ${platne}.` : `${zapytania}, ${platne}. Reszta to darmowe sprawdzenie łączności.`;
}

/** Zdanie pod wynikiem sondy: ile zapytan faktycznie poszlo i do ilu dostawcow. */
export function opisWynikuSondy(w: WynikSondyDto): string {
  return opisZapytanIDostawcow(
    w.wyslano,
    w.dostawcy.filter((d) => d.stan !== "nieskonfigurowany"),
    "Odpytano",
    "Wysłano",
  );
}
